import { Button, Chip, Stack } from '@mui/material';
import { compact, isEmpty, omit, values } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { casingFormat } from 'common/utils';
import { updateNonRoutableRequestsFilter } from 'store/thunks/workQueueThunks';

import {
  NON_ROUTABLE_REASON_LABEL,
  NON_ROUTABLE_SOURCE_LABEL,
  NON_ROUTABLE_STATUS_LABEL,
  labelOf,
} from '../constants/nonRoutableRequests';
import { isoToDateInput } from '../utils/nonRoutableRequestDates';

/**
 * Active-filter chips for the holding queue.
 *
 * Every applied filter gets a visible, removable chip, and this queue ships NO default filter, so
 * nothing can quietly hide a parked prescription behind an empty-looking screen. (The filter is
 * not persisted — the workQueue persist whitelist is `preset`, `previousPreset` and
 * `showLimitedTabs` only — so it lasts a single browsing session and starts empty in the next.)
 *
 * Removing a chip DELETES the key rather than setting it to `undefined`. Both look the same to
 * the API layer, but `{ statuses: undefined }` is not an empty object, so anything asking
 * "are there filters?" with `isEmpty` would answer yes to a bar showing no chips — which is how
 * the empty state ended up claiming filters were hiding rows when the last chip had just been
 * removed. An empty ARRAY would be worse still: on the wire that reads as "exclude everything".
 */
const NonRoutableRequestsQueueFilterStatusBar = () => {
  const dispatch = useDispatch();
  const filter = useSelector(({ workQueue }) => workQueue.nonRoutableRequests?.filter);
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const removeFilter = (filterName) => () =>
    dispatch(updateNonRoutableRequestsFilter({ filter: omit(filter, filterName) }));

  const removeAllFilter = () => dispatch(updateNonRoutableRequestsFilter({ filter: {} }));

  const partnerNames = (ids) =>
    (partners ?? [])
      .filter((entry) => ids.includes(entry.partnerUuid))
      .map((entry) => casingFormat(entry.partnerName))
      .join(', ');

  const hasActiveFilters = !isEmpty(compact(values(filter)));

  return (
    <Stack spacing={1} direction='row' flexWrap='wrap' rowGap={1} alignItems='center'>
      {!isEmpty(filter?.statuses) && (
        <Chip
          label={`Status = ${filter.statuses
            .map((s) => labelOf(NON_ROUTABLE_STATUS_LABEL, s))
            .join(', ')}`}
          onDelete={removeFilter('statuses')}
        />
      )}

      {!isEmpty(filter?.reasons) && (
        <Chip
          label={`Reason = ${filter.reasons
            .map((r) => labelOf(NON_ROUTABLE_REASON_LABEL, r))
            .join(', ')}`}
          onDelete={removeFilter('reasons')}
        />
      )}

      {!isEmpty(filter?.sources) && (
        <Chip
          label={`Source = ${filter.sources
            .map((value) => labelOf(NON_ROUTABLE_SOURCE_LABEL, value))
            .join(', ')}`}
          onDelete={removeFilter('sources')}
        />
      )}

      {filter?.referenceId && (
        <Chip
          label={`Reference Id = ${filter.referenceId}`}
          onDelete={removeFilter('referenceId')}
        />
      )}

      {!isEmpty(filter?.partnerIds) && (
        <Chip
          label={`Partner = ${
            partnerNames(filter.partnerIds) || `${filter.partnerIds.length} selected`
          }`}
          onDelete={removeFilter('partnerIds')}
        />
      )}

      {filter?.parkedAfter && (
        <Chip
          label={`Parked on or after ${isoToDateInput(filter.parkedAfter)}`}
          onDelete={removeFilter('parkedAfter')}
        />
      )}

      {filter?.parkedBefore && (
        <Chip
          label={`Parked on or before ${isoToDateInput(filter.parkedBefore)}`}
          onDelete={removeFilter('parkedBefore')}
        />
      )}

      {hasActiveFilters && <Button onClick={removeAllFilter}>Remove All</Button>}
    </Stack>
  );
};

export default NonRoutableRequestsQueueFilterStatusBar;
